import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaShieldAlt } from '@react-icons/all-files/fa/FaShieldAlt';
import { FaSitemap } from '@react-icons/all-files/fa/FaSitemap';
import Breadcrumb from '../commons/Breadcrumb';
import { useAppSelector } from '../store';
import { hasAnyAuthority } from '../shared/authorization';
import { APP_ROUTE_ENTRIES } from './app-routes.registry';

const SEG = '/configuracion/seguridad';

const cell: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 13,
  verticalAlign: 'top',
  borderBottom: '1px solid #f1f5f9',
};
const th: React.CSSProperties = {
  ...cell,
  fontWeight: 600,
  color: '#475569',
  background: '#f8fafc',
  textAlign: 'left',
};
const badge: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 600,
  marginRight: 6,
  marginBottom: 4,
  background: '#e0e7ff',
  color: '#3730a3',
};
const groupRow: React.CSSProperties = {
  padding: '14px 12px 8px',
  fontSize: 12,
  fontWeight: 700,
  color: '#64748b',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  background: '#fff',
};
const pathMono: React.CSSProperties = { fontFamily: 'ui-monospace, monospace', fontSize: 12, color: '#0f172a' };
const ok: React.CSSProperties = { color: '#059669', fontWeight: 600 };
const no: React.CSSProperties = { color: '#b91c1c', fontWeight: 600 };

/** Listado de rutas con permisos declarativos y comparación con la sesión actual. */
export default function AppRoutesDirectory() {
  const authorities = useAppSelector(s => s.authentication.account.authorities);
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return APP_ROUTE_ENTRIES;
    return APP_ROUTE_ENTRIES.filter(
      e =>
        e.path.toLowerCase().includes(t) ||
        e.label.toLowerCase().includes(t) ||
        e.group.toLowerCase().includes(t) ||
        e.requiredAuthorities.some(a => a.toLowerCase().includes(t))
    );
  }, [q]);

  const groups = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    for (const row of filtered) {
      if (!map.has(row.group)) map.set(row.group, []);
      map.get(row.group)!.push(row);
    }
    return map;
  }, [filtered]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Breadcrumb
        items={[
          { label: 'Inicio', to: '/', icon: <FaHome /> },
          { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
          { label: 'Seguridad', to: SEG, icon: <FaShieldAlt /> },
          { label: 'Mapa de rutas', icon: <FaSitemap /> },
        ]}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>Rutas y permisos</h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
            Lista declarativa en <code style={{ fontSize: 12 }}>app-routes.registry.ts</code>. Vacío = cualquier usuario con
            autoridades en sesión (misma idea que el sidebar). El API sigue siendo la fuente de verdad.
          </p>
        </div>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 400 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>Filtrar</span>
        <input
          type="search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Ruta, etiqueta, grupo o rol…"
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            fontSize: 14,
          }}
        />
      </label>

      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Ruta</th>
              <th style={th}>Descripción</th>
              <th style={th}>Permisos requeridos</th>
              <th style={th}>Visible para ti (UI)</th>
            </tr>
          </thead>
          <tbody>
            {Array.from(groups.entries()).map(([groupName, rows]) => (
              <React.Fragment key={groupName}>
                <tr>
                  <td colSpan={4} style={groupRow}>
                    {groupName}
                  </td>
                </tr>
                {rows.map(entry => {
                  const can = hasAnyAuthority(authorities, entry.requiredAuthorities);
                  const linkable = !entry.path.includes(':');
                  return (
                    <tr key={`${groupName}-${entry.path}`}>
                      <td style={cell}>
                        {linkable ? (
                          <Link to={entry.path} style={{ ...pathMono, color: '#2563eb', textDecoration: 'none' }}>
                            {entry.path}
                          </Link>
                        ) : (
                          <span style={pathMono}>{entry.path}</span>
                        )}
                      </td>
                      <td style={cell}>{entry.label}</td>
                      <td style={cell}>
                        {entry.requiredAuthorities.length === 0 ? (
                          <span style={{ color: '#64748b', fontSize: 13 }}>Ninguno específico</span>
                        ) : (
                          entry.requiredAuthorities.map(a => (
                            <span key={a} style={badge}>
                              {a}
                            </span>
                          ))
                        )}
                      </td>
                      <td style={cell}>
                        <span style={can ? ok : no}>{can ? 'Sí' : 'No'}</span>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
